// src/pages/admin/grn/CreateGRN.jsx - UPDATED VERSION
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { 
  FileText, 
  CheckCircle, 
  RefreshCw, 
  AlertCircle, 
  Package, 
  Info,
  Calendar,
  User,
  Plus,
  ChevronDown
} from 'lucide-react'
import GRNService from '../../../services/grnService'
import RawMaterialsService from '../../../services/rawMaterialsService'
import { GRNModel } from '../../../utils/grnModel'

const CreateGRN = () => {
  const navigate = useNavigate()

  // Safe number conversion helper
  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '' || isNaN(value)) {
      return defaultValue
    }
    const num = parseFloat(value)
    return isNaN(num) || !isFinite(num) ? defaultValue : num
  }

  // Safe toFixed helper
  const safeToFixed = (value, digits = 2) => {
    const num = safeNumber(value, 0)
    try {
      return num.toFixed(digits)
    } catch (error) {
      console.warn('safeToFixed error:', error, value)
      return '0.' + '0'.repeat(digits)
    }
  }

  // Safe currency formatting
  const formatCurrency = (value) => {
    return `Rs.${safeToFixed(value, 2)}`
  }

  // Helper for current date + 6 months in datetime-local format (YYYY-MM-DDTHH:mm)
  const getDefaultExpiryDate = () => {
    const now = new Date()
    const targetMonth = now.getMonth() + 6
    const d = new Date(now)
    d.setMonth(targetMonth)
    // If day rolled over (e.g. Aug 31 -> Mar 3 because Feb only has 28 days), clamp to last day of intended month
    if (d.getMonth() !== (targetMonth % 12)) {
      d.setDate(0)
    }
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Helper for min datetime-local format (current date & time in local time)
  const getMinDateTimeLocal = () => {
    const d = new Date()
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // State management - FIXED: using 'note' instead of 'notes'
  const [formData, setFormData] = useState({
    grnNumber: '',
    purchaseOrder: '',
    receivedBy: '',
    receivedDate: new Date().toISOString().split('T')[0],
    note: '', // API uses 'note' not 'notes' 
    receivedItems: []
  })

  const [availablePOs, setAvailablePOs] = useState([])
  const [selectedPO, setSelectedPO] = useState(null)
  const [loading, setLoading] = useState(false)
  const [poLoading, setPOLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [toast, setToast] = useState(null)

  // Show toast notification with enhanced types
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), type === 'info' ? 4000 : 3000) // Info messages stay longer
  }, [])

  // Generate GRN number
  useEffect(() => {
    try {
      const grnNumber = GRNModel.generateGRNNumber ? GRNModel.generateGRNNumber() : `GRN-${Date.now()}`
      setFormData(prev => ({
        ...prev,
        grnNumber: grnNumber
      }))
    } catch (error) {
      console.error('Error generating GRN number:', error)
      setFormData(prev => ({
        ...prev,
        grnNumber: `GRN-${Date.now()}`
      }))
    }
  }, [])

  // UPDATED: Fetch available purchase orders - NOW FETCHES PENDING AND APPROVED POs
  const fetchAvailablePOs = useCallback(async () => {
    try {
      setPOLoading(true)
      console.log('📦 Fetching pending and approved purchase orders for GRN creation...')
      
      // Fetch all purchase orders (no status filter)
      const response = await GRNService.getAvailablePurchaseOrders()
      
      if (response?.success) {
        // Handle different possible response structures
        let pos = []
        
        if (response.data?.purchaseOrders) {
          pos = response.data.purchaseOrders
          console.log('📦 Using purchaseOrders field from API response')
        } else if (response.data?.data) {
          pos = response.data.data
          console.log('📦 Using nested data field')
        } else if (Array.isArray(response.data)) {
          pos = response.data
          console.log('📦 Using direct data array')
        } else {
          console.warn('📦 Unexpected PO response structure:', response.data)
          pos = []
        }
        
        // Filter based on supplier type and requesting branch type:
        // - Direct supplier POs (for outlet OR warehouse branch): show 'approved' POs
        // - Warehouse-sourced POs (outlets ordering from warehouses): show 'warehouse_approved' POs
        const availablePOs = Array.isArray(pos) ? pos.filter(po => {
          const status = (po.status || 'pending').toLowerCase().trim()
          const requestingBranchType = po.requestingBranch?.type
          const supplierType = (po.supplierType || '').toLowerCase().trim()
          
          let isMatch = false
          let matchReason = ''
          
          // Direct supplier orders need to be in "approved" status
          if (supplierType === 'supplier') {
            isMatch = status === 'approved'
            matchReason = isMatch ? 'direct supplier PO with approved status' : 'direct supplier PO not approved'
          }
          // Warehouse-sourced orders need to be in "warehouse_approved" status
          else if (supplierType === 'warehouse') {
            isMatch = status === 'warehouse_approved'
            matchReason = isMatch ? 'warehouse-sourced PO with warehouse_approved status' : 'warehouse-sourced PO not warehouse_approved'
          }
          // Fallback based on requesting branch type if supplierType is unspecified
          else if (requestingBranchType === 'outlet' || requestingBranchType === 'branch') {
            isMatch = status === 'warehouse_approved' || status === 'approved'
            matchReason = isMatch ? 'outlet branch match' : 'outlet branch not approved'
          } else if (requestingBranchType === 'warehouse') {
            isMatch = status === 'approved'
            matchReason = isMatch ? 'warehouse branch match' : 'warehouse branch not approved'
          } else {
            isMatch = status === 'approved' || status === 'warehouse_approved'
            matchReason = isMatch ? 'general approved status' : 'not approved'
          }
          
          console.log('📦 PO Filter Check:', {
            poNumber: po.poNumber,
            originalStatus: po.status,
            normalizedStatus: status,
            supplierType: po.supplierType,
            requestingBranchType: requestingBranchType,
            matchReason: matchReason,
            included: isMatch
          })
          return isMatch
        }) : []
        
        setAvailablePOs(availablePOs)
        console.log('✅ Fetched', pos.length, 'total POs, showing', availablePOs.length, 'available POs for GRN')
        
        // Log status breakdown for debugging
        const statusCounts = pos.reduce((acc, po) => {
          const status = (po.status || 'pending').toLowerCase().trim()
          acc[status] = (acc[status] || 0) + 1
          return acc
        }, {})
        console.log('📊 PO Status Breakdown:', statusCounts)
        
        // Show informative message if no available POs are found
        if (availablePOs.length === 0 && pos.length > 0) {
          showToast('No purchase orders ready for GRN creation.', 'info')
        } else if (availablePOs.length === 0) {
          showToast('No purchase orders available for GRN creation', 'info')
        }
      } else {
        console.error('❌ Failed to fetch purchase orders:', response)
        showToast('Failed to fetch purchase orders', 'error')
      }
    } catch (error) {
      console.error('❌ Error fetching purchase orders:', error)
      showToast('Error fetching purchase orders', 'error')
    } finally {
      setPOLoading(false)
    }
  }, [showToast])

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Handle purchase order selection - ENHANCED
  const handlePOSelection = async (poId) => {
    try {
      const po = availablePOs.find(p => p._id === poId)
      setSelectedPO(po)
      
      if (po && Array.isArray(po.orderItems)) {
        console.log('📦 Processing PO orderItems:', po.orderItems)
        
        const processedItemsPromises = po.orderItems.map(async item => {
          console.log('📦 Processing orderItem:', {
            _id: item._id,
            itemType: item.itemType,
            productId: item.productId,
            rawMaterialId: item.rawMaterialId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            productSnapshot: item.productSnapshot,
            rawMaterialSnapshot: item.rawMaterialSnapshot
          })
          
          // Try to get unit price from multiple sources
          let defaultUnitPrice = 0
          
          // First priority: Check if the PO item itself has a unitPrice
          if (item.unitPrice && item.unitPrice > 0) {
            defaultUnitPrice = safeNumber(item.unitPrice, 0)
            console.log('✅ Using PO item unitPrice:', defaultUnitPrice)
          }
          // For raw materials, fetch from API to get current unit price
          else if (item.itemType === 'raw_material' && item.rawMaterialId) {
            try {
              console.log('📦 Fetching raw material details for ID:', item.rawMaterialId)
              const response = await RawMaterialsService.getRawMaterialById(item.rawMaterialId)
              
              console.log('📦 Raw material API response:', response)
              console.log('📦 Response data:', response?.data)
              
              if (response?.success && response.data) {
                const rawMaterial = response.data.rawMaterial || response.data
                console.log('📦 Raw material FULL object:', JSON.stringify(rawMaterial, null, 2))
                console.log('📦 Raw material cost:', rawMaterial.cost)
                console.log('📦 Raw material price:', rawMaterial.price)
                console.log('📦 Raw material unitPrice:', rawMaterial.unitPrice)
                console.log('📦 Raw material purchasePrice:', rawMaterial.purchasePrice)
                
                // Try different possible field names
                const possiblePrice = rawMaterial.cost || rawMaterial.price || rawMaterial.unitPrice || rawMaterial.purchasePrice
                
                if (possiblePrice && possiblePrice > 0) {
                  defaultUnitPrice = safeNumber(possiblePrice, 0)
                  console.log('✅ Using raw material price from API:', defaultUnitPrice)
                } else {
                  console.warn('⚠️ Raw material has no cost/price in API response:', rawMaterial)
                }
              } else {
                console.warn('⚠️ Invalid API response structure:', response)
              }
            } catch (apiError) {
              console.error('❌ Error fetching raw material from API:', apiError)
              // Fallback to snapshot cost if API fails
              if (item.rawMaterialSnapshot?.cost) {
                defaultUnitPrice = safeNumber(item.rawMaterialSnapshot.cost, 0)
                console.log('📦 Fallback: Using rawMaterialSnapshot cost:', defaultUnitPrice)
              }
            }
          }
          // For products or if raw material fetch failed, use existing logic
          else if (item.unitPrice && item.unitPrice > 0) {
            defaultUnitPrice = safeNumber(item.unitPrice, 0)
            console.log('📦 Using PO item unitPrice:', defaultUnitPrice)
          }
          else if (item.rawMaterialSnapshot?.cost) {
            defaultUnitPrice = safeNumber(item.rawMaterialSnapshot.cost, 0)
            console.log('📦 Using rawMaterialSnapshot cost:', defaultUnitPrice)
          }
          else if (item.productSnapshot?.cost) {
            defaultUnitPrice = safeNumber(item.productSnapshot.cost, 0)
            console.log('📦 Using productSnapshot cost:', defaultUnitPrice)
          }
          
          console.log('📦 Final default unit price:', defaultUnitPrice)
          
          return {
            // Store the PO item ID for API submission
            poItemId: item._id, // This is the purchase order item ID needed by API
            itemType: item.itemType || 'product',
            productId: item.productId || null,
            rawMaterialId: item.rawMaterialId || null,
            // Get item name from snapshot
            itemName: item.productSnapshot?.name || item.rawMaterialSnapshot?.name || 'Unknown Item',
            itemCode: item.productSnapshot?.sku || item.rawMaterialSnapshot?.code || 'N/A',
            orderedQuantity: safeNumber(item.quantity, 0),
            unit: item.productSnapshot?.unit?.abbreviation || item.rawMaterialSnapshot?.unit?.abbreviation || 'pcs',
            receivedQuantity: '',
            unitPrice: defaultUnitPrice, // Pre-populate with unit price (user can still edit)
            totalPrice: 0,
            expiryDate: getDefaultExpiryDate() // Default expiry date: current date + 6 months
          }
        })
        
        // Wait for all promises to resolve
        const processedItems = await Promise.all(processedItemsPromises)
        
        console.log('📦 Processed items for GRN:', processedItems)
        
        setFormData(prev => ({
          ...prev,
          purchaseOrder: poId,
          receivedItems: processedItems
        }))
      } else {
        console.warn('📦 No orderItems found in PO:', po)
        setFormData(prev => ({
          ...prev,
          purchaseOrder: poId,
          receivedItems: []
        }))
      }
    } catch (error) {
      console.error('Error handling PO selection:', error)
      setFormData(prev => ({
        ...prev,
        purchaseOrder: poId,
        receivedItems: []
      }))
    }
  }

  // Handle received quantity change
  const handleReceivedQuantityChange = (index, quantity) => {
    try {
      const currentItem = formData.receivedItems[index]
      const orderedQuantity = safeNumber(currentItem?.orderedQuantity, 0)
      const numQuantity = safeNumber(quantity, 0)
      
      if (numQuantity > orderedQuantity) {
        showToast(`Cannot receive more than ordered quantity (${orderedQuantity})`, 'warning')
        return
      }

      setFormData(prev => ({
        ...prev,
        receivedItems: prev.receivedItems.map((item, i) => 
          i === index ? {
            ...item,
            receivedQuantity: quantity,
            // Still calculate totalPrice for display purposes
            totalPrice: numQuantity * safeNumber(item.unitPrice, 0)
          } : item
        )
      }))
    } catch (error) {
      console.error('Error updating received quantity:', error)
    }
  }

  // Handle unit price change - NEW FUNCTION
  const handleUnitPriceChange = (index, price) => {
    try {
      const numPrice = safeNumber(price, 0)
      
      // Show warning if price is 0 and there's a received quantity
      const currentItem = formData.receivedItems[index]
      if (numPrice <= 0 && safeNumber(currentItem?.receivedQuantity, 0) > 0) {
        showToast('Unit price must be greater than 0 for received items', 'error')
      }
      
      setFormData(prev => ({
        ...prev,
        receivedItems: prev.receivedItems.map((item, i) => 
          i === index ? {
            ...item,
            unitPrice: price,
            totalPrice: safeNumber(item.receivedQuantity, 0) * numPrice
          } : item
        )
      }))
    } catch (error) {
      console.error('Error updating unit price:', error)
    }
  }

  // Handle expiry date change
  const handleExpiryDateChange = (index, date) => {
    try {
      setFormData(prev => ({
        ...prev,
        receivedItems: prev.receivedItems.map((item, i) => 
          i === index ? {
            ...item,
            expiryDate: date
          } : item
        )
      }))
    } catch (error) {
      console.error('Error updating expiry date:', error)
    }
  }

  // Calculate totals safely - ADDED FROM WORKING VERSION
  const calculateTotals = () => {
    try {
      if (GRNModel.calculateTotals && typeof GRNModel.calculateTotals === 'function') {
        const result = GRNModel.calculateTotals(formData.receivedItems)
        return result || {
          totalReceived: 0,
          completeItems: 0,
          partialItems: 0,
          incompleteItems: 0,
          totalItems: 0
        }
      } else {
        // Fallback calculation if GRNModel method doesn't exist
        const items = Array.isArray(formData.receivedItems) ? formData.receivedItems : []
        const totalReceived = items.reduce((sum, item) => sum + safeNumber(item.totalPrice, 0), 0)
        const completeItems = items.filter(item => safeNumber(item.receivedQuantity, 0) === safeNumber(item.orderedQuantity, 0)).length
        const partialItems = items.filter(item => {
          const received = safeNumber(item.receivedQuantity, 0)
          const ordered = safeNumber(item.orderedQuantity, 0)
          return received > 0 && received < ordered
        }).length
        const incompleteItems = items.filter(item => safeNumber(item.receivedQuantity, 0) === 0).length
        
        return {
          totalReceived,
          completeItems,
          partialItems,
          incompleteItems,
          totalItems: items.length
        }
      }
    } catch (error) {
      console.error('Error calculating totals:', error)
      return {
        totalReceived: 0,
        completeItems: 0,
        partialItems: 0,
        incompleteItems: 0,
        totalItems: 0
      }
    }
  }

  // Calculate total amount - NEW FUNCTION
  const calculateTotalAmount = () => {
    return formData.receivedItems.reduce((total, item) => 
      total + safeNumber(item.totalPrice, 0), 0
    )
  }

  // Validate form
  const validateForm = () => {
    try {
      if (GRNModel.validate && typeof GRNModel.validate === 'function') {
        const validation = GRNModel.validate(formData)
        setErrors(validation.errors || {})
        return validation.isValid || false
      } else {
        // Fallback validation
        const newErrors = {}
        
        if (!formData.purchaseOrder) {
          newErrors.purchaseOrder = 'Please select a purchase order'
        }
        
        if (!formData.receivedBy?.trim()) {
          newErrors.receivedBy = 'Received by is required'
        }
        
        if (!formData.receivedDate) {
          newErrors.receivedDate = 'Received date is required'
        }
        
        // Validate that at least one item has received quantity > 0
        const hasReceivedItems = formData.receivedItems.some(item => safeNumber(item.receivedQuantity, 0) > 0)
        if (!hasReceivedItems) {
          newErrors.receivedItems = 'Please enter received quantities for at least one item'
        }
        
        // Validate unit prices - cannot be 0 for items with received quantity > 0
        const itemsWithInvalidPrice = formData.receivedItems.filter(item => 
          safeNumber(item.receivedQuantity, 0) > 0 && safeNumber(item.unitPrice, 0) <= 0
        )
        if (itemsWithInvalidPrice.length > 0) {
          newErrors.unitPrice = 'Unit price must be greater than 0 for all received items'
        }
        
        // Validate expiry dates - required for items with received quantity > 0
        const itemsWithoutExpiryDate = formData.receivedItems.filter(item => 
          safeNumber(item.receivedQuantity, 0) > 0 && (!item.expiryDate || item.expiryDate.trim() === '')
        )
        if (itemsWithoutExpiryDate.length > 0) {
          newErrors.expiryDate = 'Expiry date is required for all received items'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
      }
    } catch (error) {
      console.error('Error validating form:', error)
      return false
    }
  }

  // Handle form submission - ENHANCED VERSION from working code
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    
    if (!validateForm()) {
      showToast('Please fix the errors before submitting', 'error')
      return false
    }

    try {
      setLoading(true)
      console.log('📦 Creating GRN with data:', formData)
      
      const safeReceivedItems = Array.isArray(formData.receivedItems) ? formData.receivedItems : []
      console.log('📦 Safe received items:', safeReceivedItems)
      
      let apiData
      try {
        if (GRNModel.formatForAPI && typeof GRNModel.formatForAPI === 'function') {
          const safeFormData = {
            ...formData,
            receivedItems: safeReceivedItems
          }
          apiData = GRNModel.formatForAPI(safeFormData)
          console.log('📦 GRNModel.formatForAPI succeeded:', apiData)
        } else {
          throw new Error('GRNModel.formatForAPI not available')
        }
      } catch (formatError) {
        console.warn('❌ GRNModel.formatForAPI failed, using fallback:', formatError)
        
        // CORRECT: Use poItemId as shown in Postman example
        const processedItems = safeReceivedItems
          .filter(item => item.poItemId && safeNumber(item.receivedQuantity, 0) > 0) // Only include items with received quantity
          .map(item => {
            const itemData = {
              poItemId: item.poItemId,
              receivedQuantity: safeNumber(item.receivedQuantity, 0),
              unitPrice: safeNumber(item.unitPrice, 0)
            }
            
            // Only include expiryDate if it has a value
            if (item.expiryDate && item.expiryDate.trim() !== '') {
              itemData.expiryDate = item.expiryDate
            }
            
            return itemData
          })
        
        if (processedItems.length === 0) {
          showToast('No valid items to receive. Please enter received quantities for at least one item.', 'error')
          return false
        }
        
        console.log('📦 Using fallback with poItemId structure:', processedItems)
        
        apiData = {
          purchaseOrder: formData.purchaseOrder,
          receivedBy: formData.receivedBy,
          receivedDate: formData.receivedDate,
          note: formData.note,
          receivedItems: processedItems
        }
      }
      
      console.log('📦 Final API data:', apiData)
      
      // Show processing message for long operations
      showToast('Creating GRN... This may take a moment.', 'info')
      
      const response = await GRNService.createGRN(apiData)
      
      if (response?.success) {
        showToast('GRN created successfully!')
        // Navigate back to GRN list after success
        setTimeout(() => {
          navigate('/admin/grn/all')
        }, 1500)
        return true
      } else {
        console.error('❌ API returned unsuccessful response:', response)
        showToast(response?.message || 'Failed to create GRN', 'error')
        return false
      }
    } catch (error) {
      console.error('❌ Error creating GRN:', error)
      
      // Display the exact backend error message
      let errorMessage = error?.message || 'Error creating GRN. Please try again.'
      
      // Only add context for network/timeout errors, otherwise use exact backend message
      if (error?.message) {
        if (error.message.includes('timeout') || error.message.includes('ECONNABORTED')) {
          errorMessage = 'Request timed out. The server may be busy. Please try again in a moment.'
        } else if (error.message.includes('Network Error') || error.message.includes('ERR_NETWORK')) {
          errorMessage = 'Network error. Please check your connection and try again.'
        }
        // For all other errors, use the exact error message from backend
      }
      
      showToast(errorMessage, 'error')
      return false
    } finally {
      setLoading(false)
    }
  }

  // Handle complete GRN
  const handleCompleteGRN = async () => {
    if (!formData.grnNumber) {
      showToast('Please create the GRN first', 'error')
      return
    }

    try {
      // First create the GRN
      const success = await handleSubmit(null)
      
      if (success) {
        console.log('✅ GRN creation completed successfully')
      } else {
        console.log('❌ GRN creation failed')
      }
    } catch (error) {
      console.error('❌ Error in complete GRN flow:', error)
      showToast('Error completing GRN', 'error')
    }
  }

  // Effects
  useEffect(() => {
    fetchAvailablePOs()
  }, [fetchAvailablePOs])

  const totals = calculateTotals()

  return (
    <div className="space-y-6">
      {/* Enhanced Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'success' 
            ? 'bg-green-100 text-green-800 border border-green-200' 
            : toast.type === 'info'
            ? 'bg-blue-100 text-blue-800 border border-blue-200'
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          <div className="flex items-center">
            {toast.type === 'success' ? (
              <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center mr-2">
                <span className="text-white text-xs">✓</span>
              </div>
            ) : toast.type === 'info' ? (
              <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center mr-2">
                <span className="text-white text-xs">i</span>
              </div>
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Goods Receipt Note</h1>

        </div>
        <div className="flex space-x-3">
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Clear All</span>
          </Button>
          <Button
            onClick={handleCompleteGRN}
            disabled={loading || !formData.receivedItems.length || !formData.receivedItems.some(item => safeNumber(item.receivedQuantity, 0) > 0)}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            <span>Complete GRN</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left Panel - GRN Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              GRN Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* GRN Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GRN Number *
              </label>
              <Input
                name="grnNumber"
                value={formData.grnNumber}
                readOnly
                className="bg-gray-50"
              />
            </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purchase Orders *
                    <span className="text-xs text-gray-500 block mt-1">
                      (Outlet branches: warehouse_approved | Warehouse branches: approved)
                    </span>
                    </label>
                    <div className="relative">
                    <select
                      name="purchaseOrder"
                      value={formData.purchaseOrder}
                      onChange={(e) => handlePOSelection(e.target.value)}
                      disabled={poLoading}
                      className={`w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none ${
                      errors.purchaseOrder ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">
                      {poLoading ? 'Loading POs...' : availablePOs.length === 0 ? 'No POs available' : 'Select purchase order'}
                      </option>
                      {availablePOs.map(po => {
                      const status = po.status?.toUpperCase() || 'PENDING'
                      const statusIcon = status === 'APPROVED' ? '✅' : '🟡'
                      return (
                        <option key={po._id} value={po._id}>
                        {statusIcon} {po.poNumber} - {po.supplier?.name || po.warehouse?.name || 'Unknown Supplier'} - {status}
                        </option>
                      )
                      })}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                    {errors.purchaseOrder && (
                    <p className="mt-1 text-sm text-red-600">{errors.purchaseOrder}</p>
                    )}
                    {poLoading && (
                    <p className="mt-1 text-sm text-gray-500">Loading purchase orders...</p>
                    )}
                    {!poLoading && availablePOs.length === 0 && (
                    <p className="mt-1 text-sm text-orange-600">
                      ℹ️ No purchase orders ready for GRN. Outlet orders need warehouse_approved status, warehouse orders need approved status.
                    </p>
                    )}
                  </div>

                  {/* Received By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Received By *
              </label>
              <Input
                name="receivedBy"
                value={formData.receivedBy}
                onChange={handleInputChange}
                placeholder="Enter receiver name"
                className={errors.receivedBy ? 'border-red-500' : ''}
              />
              {errors.receivedBy && (
                <p className="mt-1 text-sm text-red-600">{errors.receivedBy}</p>
              )}
            </div>

            {/* Received Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Received Date *
                <span className="text-xs text-gray-500 block mt-1">
                  (Future dates cannot be selected)
                </span>
              </label>
              <Input
                type="date"
                name="receivedDate"
                value={formData.receivedDate}
                onChange={handleInputChange}
                max={new Date().toISOString().split('T')[0]}
                className={errors.receivedDate ? 'border-red-500' : ''}
              />
              {errors.receivedDate && (
                <p className="mt-1 text-sm text-red-600">{errors.receivedDate}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any discrepancies, damages, or special notes.."
              />
            </div>
          </CardContent>
        </Card>

        {/* Right Panel - Receipt Summary & Received Items */}
        <div className="space-y-6">
          {/* Receipt Summary - ADDED FROM WORKING VERSION */}
          <Card>
            <CardHeader>
              <CardTitle>Receipt Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-sm text-gray-600">Complete:</div>
                  <div className="text-2xl font-bold text-green-600">{safeNumber(totals.completeItems, 0)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Partial:</div>
                  <div className="text-2xl font-bold text-blue-600">{safeNumber(totals.partialItems, 0)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Incomplete:</div>
                  <div className="text-2xl font-bold text-red-600">{safeNumber(totals.incompleteItems, 0)}</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium">Items Status:</span>
                  <span className="text-xl font-bold">
                    {safeNumber(totals.completeItems, 0) + safeNumber(totals.partialItems, 0)} / {safeNumber(totals.totalItems, 0)} processed
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {safeNumber(totals.totalItems, 0)} total items
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Received Items - UPDATED LAYOUT */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center">
                  <Package className="h-5 w-5 mr-2" />
                  Received Items
                </div>
                {formData.receivedItems.length > 0 && (
                  <span className="text-sm text-gray-500">
                    {formData.receivedItems.length} items
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {formData.receivedItems.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Select a purchase order to view items</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Items List - NEW LAYOUT WITH INDIVIDUAL CARDS */}
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {formData.receivedItems.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-medium text-gray-900">{item.itemName} - <span className='text-gray-500'>{item.itemCode}</span></h4>
                            {/* <p className="text-sm text-gray-500">Code: {item.itemCode}</p> */}
                            <p className="text-sm text-gray-500">Ordered: {item.orderedQuantity} {item.unit}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Received Quantity
                            </label>
                            <Input
                              type="number"
                              min="0"
                              max={item.orderedQuantity}
                              step="any"
                              value={item.receivedQuantity ?? ''}
                              onChange={(e) => handleReceivedQuantityChange(index, e.target.value)}
                              placeholder="0"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Unit Price
                            </label>
                            <Input
                              type="number"
                              min="0.01"
                              step="any"
                              value={item.unitPrice === 0 ? '' : item.unitPrice}
                              onChange={(e) => handleUnitPriceChange(index, e.target.value)}
                              placeholder="0.00"
                              className={`text-sm ${
                                safeNumber(item.receivedQuantity, 0) > 0 && safeNumber(item.unitPrice, 0) <= 0 
                                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                  : ''
                              }`}
                            />
                            {safeNumber(item.receivedQuantity, 0) > 0 && safeNumber(item.unitPrice, 0) <= 0 && (
                              <p className="text-xs text-red-600 mt-1">
                                Unit price must be greater than 0
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Expiry Date & Time *
                          </label>
                          <Input
                            type="datetime-local"
                            value={item.expiryDate}
                            onChange={(e) => handleExpiryDateChange(index, e.target.value)}
                            min={getMinDateTimeLocal()}
                            className={`text-sm ${
                              safeNumber(item.receivedQuantity, 0) > 0 && (!item.expiryDate || item.expiryDate.trim() === '')
                                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                : ''
                            }`}
                          />
                          {safeNumber(item.receivedQuantity, 0) > 0 && (!item.expiryDate || item.expiryDate.trim() === '') && (
                            <p className="text-xs text-red-600 mt-1">
                              Expiry date and time is required for received items
                            </p>
                          )}
                        </div>
                        
                        {safeNumber(item.receivedQuantity, 0) > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">Total:</span>
                              <span className="font-medium">{formatCurrency(item.totalPrice)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Total Summary */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium text-gray-900">Total Amount:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(calculateTotalAmount())}
                      </span>
                    </div>
                  </div>

                  {errors.receivedItems && (
                    <p className="text-sm text-red-600">{errors.receivedItems}</p>
                  )}
                  {errors.unitPrice && (
                    <p className="text-sm text-red-600">{errors.unitPrice}</p>
                  )}
                  {errors.expiryDate && (
                    <p className="text-sm text-red-600">{errors.expiryDate}</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default CreateGRN